import { CartItem, ProductOptionRangeType } from '@pedix-workspace/utils';
import { OrderItem, OrderItemRow } from '../order-message.types';
import {
  getItemPrice,
  getItemType,
  getOptionItemMultiplier,
  getOptionItemQuantity,
  getOptionItemTotal,
  getOptionItemUnitPrice,
  getProductOrSinglePresentationQuantity,
  getProductOrSinglePresentationTotal,
  getProductOrSinglePresentationUnitPrice,
} from './cart-item-utils';

export function getCartItemsByCategory(
  cartItems: CartItem[],
): Record<string, Record<string, CartItem[]>> {
  const itemsByCategory: Record<string, Record<string, CartItem[]>> = {};

  cartItems.map(cartItem => {
    const categoryName = cartItem.category.name;
    const groupName = cartItem.product.groupName || 'undefined';

    if (!itemsByCategory[categoryName]) {
      itemsByCategory[categoryName] = {};
    }
    if (!itemsByCategory[categoryName][groupName]) {
      itemsByCategory[categoryName][groupName] = [];
    }
    itemsByCategory[categoryName][groupName].push(cartItem);
  });

  return itemsByCategory;
}

export function sortAsscending(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  if (a === 'undefined') {
    return 1;
  }
  if (b === 'undefined') {
    return -1;
  }
  return a < b ? -1 : 1;
}

/**
 * Expands a single OrderItem into one or more rows. Centralizes the type-discrimination
 * logic used by the cart-view UI, Excel reports and billing line items.
 */
export function getOrderItemRows(orderItem: OrderItem): OrderItemRow[] {
  const cart = orderItem.cartItem;

  switch (orderItem.type) {
    case 'regular':
      return [
        {
          itemId: cart.product.id,
          sku: cart.product.sku,
          presentationIndex: null,
          presentationSource: null,
          quantity: orderItem.quantity ?? 0,
          total: orderItem.total ?? 0,
          totalWithoutDiscount: getProductOrSinglePresentationTotal(cart, {
            usePriceDiscount: false,
          }),
          unitPrice: orderItem.unitPrice ?? 0,
        },
      ];

    case 'single-presentation': {
      const presentation = cart.selectedPresentation!.items[0];
      const presentationOption = cart.selectedPresentation!.option;
      const updatesCartItemQty =
        presentationOption.rangeType === ProductOptionRangeType.FIXED &&
        presentationOption.min === 1;
      return [
        {
          itemId: presentation.id,
          sku: presentation.sku,
          presentationIndex: updatesCartItemQty ? null : 0,
          presentationSource: presentation,
          quantity: orderItem.quantity ?? 0,
          total: orderItem.total ?? 0,
          totalWithoutDiscount: getProductOrSinglePresentationTotal(cart, {
            usePriceDiscount: false,
          }),
          unitPrice: orderItem.unitPrice ?? 0,
        },
      ];
    }

    case 'multi-presentation': {
      const selectedPresentation = cart.selectedPresentation!;
      const presentations = orderItem.presentations!;
      return selectedPresentation.items.map((sourceItem, index) => {
        const presItem = presentations.items[index];
        const quantity = sourceItem.quantity || 1;
        return {
          itemId: sourceItem.id,
          sku: sourceItem.sku,
          presentationIndex: index,
          presentationSource: sourceItem,
          quantity: presItem.quantity,
          total: presItem.price,
          totalWithoutDiscount: getItemPrice(sourceItem, { usePriceDiscount: false }) * quantity,
          unitPrice: presItem.unitPrice,
        };
      });
    }
  }
}

export function endOrderCartItemToOrderItem(cartItem: CartItem): OrderItem {
  const itemType = getItemType(cartItem);

  return {
    cartItem: cartItem,
    productName: cartItem.product.name,
    total: getProductOrSinglePresentationTotal(cartItem, { usePriceDiscount: true }),
    quantity: getProductOrSinglePresentationQuantity(cartItem),
    unitPrice: getProductOrSinglePresentationUnitPrice(cartItem, { usePriceDiscount: true }),
    observations: cartItem.observations,
    type: itemType,
    options: cartItem.selectedOptions?.map(({ option, items }) => ({
      name: option.name,
      items: items.map(item => {
        const productMultiplier = getOptionItemMultiplier(cartItem);

        return {
          name: item.name,
          quantity: getOptionItemQuantity(option, item, productMultiplier),
          price: getOptionItemTotal(option, item, productMultiplier, { usePriceDiscount: true }),
          unitPrice: getOptionItemUnitPrice(item, { usePriceDiscount: true }),
          aliquot: item.aliquot,
        };
      }),
    })),
    presentationName:
      itemType === 'single-presentation' ? cartItem.selectedPresentation.items[0].name : undefined,
    presentations:
      itemType === 'multi-presentation'
        ? {
            items: cartItem.selectedPresentation.items.map(item => ({
              name: item.name,
              sku: item.sku,
              quantity: item.quantity || 1,
              price: getItemPrice(item, { usePriceDiscount: true }) * (item.quantity || 1),
              unitPrice: getItemPrice(item, { usePriceDiscount: true }),
            })),
          }
        : undefined,
  };
}
